import axios from 'axios';
import gql from 'graphql-tag';
import Cookies from 'js-cookie';
import _ from 'lodash';

import AuthService from './auth-service';
import ErrorService from './error-service';
import ToastService from './toast-service';
import StorageService from './storage-service';

// import pako from 'pako';

/**
 * What the API answers, with a 403, to a session that still owes a passkey and asked for anything
 * but the endpoints that get one. Mirrors `Http::PASSKEY_ENROLLMENT_PENDING` on the API.
 */
export const PASSKEY_ENROLLMENT_PENDING = 'Set up a passkey to continue';

let api = {
	client: null,
	graphql: {},
	clerk: null
};

api.client = axios.create({
	baseURL: `${window.apiBase}/api`,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

// function getUrl(config) {
// 	const range = config?.headers['X-Range'] ? `:${config.headers['X-Range']}` : '';
// 	return `${config.url}${range}`;
// }

// api.client.interceptors.request.use((config) => {

// 	const data = sessionStorage.getItem(getUrl(config));
// 	const etag = data ? JSON.parse(data).etag : null;

// 	if(etag) {
// 		config.headers['If-None-Match'] = etag;
// 	}

// 	return config;
// });

/**
 * Adds an interceptor to the API client's request configuration.
 *
 * This interceptor performs the following actions before a request is sent:
 * 1. Checks if the `clerk` object is defined. If not, it returns the original config.
 * 2. Retrieves a token from the `clerk` session.
 * 3. If a token is retrieved and the request targets a Pixwel API host,
 *    it adds the token to the request headers as an "Authorization" header.
 *
 * @param {Object} config - The Axios request configuration object.
 * @returns {Object} The modified or original request configuration object.
 * @throws {Promise} A rejected promise if an error occurs.
 */
const shouldAttachToken = (config) => {
	const hosts = new Set();
	const addHost = (value) => {
		if(!value) {
			return;
		}
		try {
			hosts.add(new URL(value, window.location.origin).host);
		}
		catch{
			// ignore values that cannot be parsed as URLs
		}
	};

	addHost(window.location.host ? `//${window.location.host}` : null);
	addHost(config.baseURL);
	addHost(api.client.defaults.baseURL);
	addHost(window.apiBase);
	addHost(window.gqlBase);

	try {
		const requestUrl = config.url.startsWith('http')
			? new URL(config.url)
			: new URL(
				config.url,
				config.baseURL ?? api.client.defaults.baseURL ?? window.location.origin
			);
		return hosts.has(requestUrl.host);
	}
	catch{
		return false;
	}
};

api.client.interceptors.request.use(
	async(config) => {
		if(!api.clerk?.session) {
			return config;
		}
		const token = await api.clerk.session.getToken();

		if(token && shouldAttachToken(config)) {
			config.headers['Authorization'] = token;
		}

		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

api.client.interceptors.response.use(
	(response) => {
		// const etag = response.headers['etag'];
		// if(etag) {
		// 	try {
		// 		const compressedData = pako.deflate(JSON.stringify(response.data));
		// 		sessionStorage.setItem(
		// 			getUrl(response.config),
		// 			JSON.stringify({ etag, data: compressedData })
		// 		);
		// 	}
		// 	catch(e) {
		// 		// eslint-disable-next-line no-console
		// 		console.info(`Exceeded quota: ${e}`);
		// 	}
		// }

		return response;
	},
	(error) => {
		if(error?.response?.status === 401) {
			handle401Error(error);
			// Don't re-throw: handle401Error removes the token and reloads the page.
			// Re-throwing would create unhandled rejections since callers don't catch 401s.
			return new Promise(() => undefined);
		}
		// else if( error?.response?.status === 304 ) {
		// 	const storedData = sessionStorage.getItem(getUrl(error.response.config));
		// 	if(storedData) {
		// 		const { data } = JSON.parse(storedData);
		// 		const decompressedData = pako.inflate(data, { to: 'string' });
		// 		error.response.data = JSON.parse(decompressedData);
		// 	}
		// 	return error.response;
		// }
		else if(error?.response?.status === 403) {
			// A session that still owes a passkey is refused everything but the enrollment
			// endpoints, and the enrollment screen is already the whole answer to that -- the
			// layout's background queries hitting it is not news worth a toast. Nor is a
			// read its caller expects may be refused (`quietForbidden` on the request).
			if(error.response?.data?.message !== PASSKEY_ENROLLMENT_PENDING && !error.config?.quietForbidden) {
				ToastService.create('error', `You do not have permission to perform this action`, {
					duration: 2000,
					toastId: 'error-403'
				});
			}
			throw error;
		}
		else if(error?.response?.status === 422) {
			const errors = error.response?.data;
			let message = 'Validation error';
			if(errors && typeof errors === 'object' && !Array.isArray(errors)) {
				const errorMessages = Object.values(errors).flat()
					.filter(Boolean);
				if(errorMessages.length) {
					message = errorMessages.join(', ');
				}
			}
			else if(typeof errors === 'string' && errors) {
				message = errors;
			}
			ToastService.create('error', message, {
				duration: 5000,
				toastId: 'error-422'
			});
			throw error;
		}
		else if(error?.message === 'Network Error') {
			ToastService.create('error', `There was a network error. Please try again`, {
				duration: 2000,
				toastId: 'error-network'
			});
			error.toastShown = true;
			throw error;
		}
		else if(
			error?.code === 'ECONNABORTED'
			|| (typeof error?.message === 'string' && error.message.includes('timeout'))
		) {
			ToastService.create('error', `The request timed out. Please try again`, {
				duration: 2000,
				toastId: 'error-timeout'
			});
			error.toastShown = true;
			throw error;
		}
		else if(error?.response?.status >= 500) {
			ToastService.create('error', `Something went wrong, please try again`, {
				duration: 3000,
				toastId: `error-${error.response.status}`
			});
			throw error;
		}
		else {
			throw error;
		}
	}
);

const handle401Error = (error) => {
	let currentLocation = window.location.pathname;
	StorageService.set('lastRoute', currentLocation);

	ToastService.create('error', `Unauthorized: please try logging in again`, {
		duration: 2000,
		toastId: 'error-401'
	});
	console.error(error);
	AuthService.removeToken();
	// Skip the reload under Cypress — repeated 401s from unmocked endpoints
	// would otherwise schedule overlapping reloads and trip the redirection limit.
	if(window.Cypress) {
		return;
	}
	setTimeout(() => window.location.reload(), 2000);
};

export function setupAPI(newSession, freshSetup) {
	let session;
	let cookieSession = Cookies.get('pixwel.api.credentials');
	let clerkSession = Cookies.get('__session');

	if(cookieSession && !freshSetup) {
		session = JSON.parse(cookieSession);
	}
	else {
		session = newSession;
	}

	if(clerkSession) {
		api.client.defaults.headers.common['Authorization'] = `${clerkSession}`;
	}
	else if(session) {
		let hash = window.btoa(`${session.username}:token-${session.token}`);
		api.client.defaults.headers.common['Authorization'] = `Basic ${hash}`;
	}

	const uncaughtStatusCodes = {
		'UNAUTHENTICATED': handle401Error,
		'BAD_USER_INPUT': (errors) => {
			ToastService.create('error', ErrorService.getErrorMessage(errors));
		}
	};

	api.graphql.request = (query, variables) => {
		let parsed = gql`
			${query}
		`;
		let opName = parsed.definitions[0].name.value;
		let url = window.Cypress ? `gql?op=${opName}` : window.gqlBase + `?${opName}`;
		return api.client.post(url, {
			query,
			variables
		}).then((res) => {
			if(res.data.errors) {
				let statusCode = _.get(res, 'data.errors[0].extensions.code');

				// If we have a status code that we know how to handle, do so
				if( _.keys(uncaughtStatusCodes).includes(statusCode) ) {
					uncaughtStatusCodes[statusCode](res.data.errors);
					return;
				}

				let errorMessage = '';
				if(_.isArray(res.data.errors)) {
					errorMessage = ErrorService.getErrorMessage(res.data.errors || []);
				}

				throw new Error(errorMessage);
			}

			if(!res.data.data && res?.request?.method === 'GET') {
				throw new Error('An unexpected error has occurred');
			}
			return res.data.data;
		})
			.catch((error) => {
				console.error(error);
				if(error?.response?.status === 401) {
					handle401Error(error);
				}
				else {
					const errorMessage = _.isString(error)
						? error
						: _.isString(error.message) && error.message !== 'error'
							? error.message
							: _.isString(error.response?.data)
								? error.response?.data
								: 'An unexpected error has occurred';
					throw new Error(errorMessage);
				}
			});
	};
}

export function buildURL(path, params) {
	let urlParams = new URLSearchParams();

	if(!params) {
		return path;
	}
	for(const [key, val] of Object.entries(params)) {
		if(typeof val === 'object') {
			for(const [_key, _val] of Object.entries(val)) {
				if(_val) {
					urlParams.append(`${key}[${_key}]`, _val);
				}
			}
		}
		else if(val || typeof val === 'boolean') {
			urlParams.append(key, val);
		}
	}

	let url = path.startsWith('/') ? path : `/${path}`;
	return Object.keys(params).length === 0
		? url
		: `${url}?${urlParams.toString()}`;
}

export const getPaginationTotal = (headerValue = '') => {
	const rangePattern = /^resources \d+-\d+\/(\d+|\*)/;
	let matches = headerValue.match(rangePattern);

	if( matches === null || _.isUndefined(matches[1]) || matches[1] === '*' ) {
		return 0;
	}

	return +matches[1];
};

/**
 * Fetches every page of a collection endpoint by following the X-Content-Range
 * response header, for callers that genuinely need the full result set — the
 * API returns a single default page when no X-Range is sent, so fetching once
 * would silently truncate large collections.
 *
 * Returns the concatenated rows (`res.data`, or `res.data.items` for report
 * endpoints that wrap their rows).
 */
export const getAllPages = async(url, config = {}, pageSize = 1000) => {
	const rows = [];
	let next = 0;
	let total = Infinity;

	while(next < total) {
		const res = await api.client.get(url, {
			...config,
			headers: {
				...(config.headers || {}),
				'X-Range': `resources=${next}-${next + pageSize - 1}`
			}
		});
		const body = res?.data;
		const page = Array.isArray(body)
			? body
			: Array.isArray(body?.items) ? body.items : null;

		if( page === null ) {
			// Not a collection shape — hand the payload back for the caller to interpret.
			return rows.length ? rows : body ?? [];
		}

		const range = /^resources (\d+)-(\d+)\/(\d+|\*)/.exec(res.headers?.['x-content-range'] ?? '');
		if( !range || range[3] === '*' ) {
			// No range to advance on — the single response is all we can get.
			rows.push(...page);
			break;
		}
		total = +range[3];

		// Advance past the range the server reports having served, not by the rows
		// received: endpoints that filter after the range is computed (e.g. resolved
		// comments) return fewer rows than the range spans, and counting rows would
		// re-request overlapping ranges or stop before the collection is exhausted.
		// A range that fails to advance would re-serve the same rows, so stop
		// before appending duplicates.
		const advanced = +range[2] + 1;
		if( advanced <= next ) {
			break;
		}
		rows.push(...page);
		next = advanced;
	}

	return rows;
};

export default api;
